import { 
  Battery, 
  Cpu, 
  Wifi,
  Shield,
  ArrowRight,
  CheckCircle2,
  Server,
  Gauge,
  Thermometer,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Technology = ({ onContactClick }) => {
  return (
    <main className="pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-white" data-testid="technology-hero">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Technologie</span>
              <h1 className="heading-xl text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Bewährte Technik für kritische Infrastruktur
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Unsere BESS-Systeme basieren auf modernster Lithium-Ionen-Technologie, 
                entwickelt für höchste Zuverlässigkeit und Sicherheit im Krankenhausbetrieb.
              </p>
              <Button 
                onClick={onContactClick}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="technology-cta-btn"
              >
                Technische Beratung anfragen
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_hospital-bess-losung/artifacts/6guqdq5d_Hospital_BESS16.png"
                  alt="Ingenieure bei der BESS-Installation"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Architecture */}
      <section className="section-padding bg-white" data-testid="system-architecture">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Systemarchitektur
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Ein BESS besteht aus vier Hauptkomponenten, die perfekt aufeinander abgestimmt sind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="card-feature text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Battery className="w-8 h-8 text-[#10B981]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Batteriemodule
                </h3>
                <p className="text-sm text-slate-600">
                  Lithium-Eisenphosphat (LFP) Zellen für maximale Sicherheit und Langlebigkeit.
                </p>
              </CardContent>
            </Card>

            <Card className="card-feature text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Server className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Wechselrichter
                </h3>
                <p className="text-sm text-slate-600">
                  Hocheffiziente Wandlung zwischen Gleichstrom (Batterie) und Wechselstrom (Netz).
                </p>
              </CardContent>
            </Card>

            <Card className="card-feature text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Cpu className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Energiemanagement
                </h3>
                <p className="text-sm text-slate-600">
                  Intelligente Steuerung optimiert automatisch Laden und Entladen.
                </p>
              </CardContent>
            </Card>

            <Card className="card-feature text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Wifi className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Monitoring
                </h3>
                <p className="text-sm text-slate-600">
                  24/7 Überwachung aller Parameter mit Fernzugriff und Alarmierung.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* System Diagram */}
          <div className="bg-slate-50 rounded-2xl p-6 lg:p-10">
            <h3 className="text-xl font-semibold text-[#0F172A] mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Systemintegration im Krankenhaus
            </h3>
            <div className="grid lg:grid-cols-5 gap-4 items-center">
              <div className="bg-white rounded-xl p-4 text-center border border-slate-200">
                <div className="text-sm font-medium text-slate-500 mb-1">Stromnetz</div>
                <div className="text-lg font-semibold text-[#0F172A]">Einspeisung</div>
              </div>
              <div className="hidden lg:flex justify-center">
                <ArrowRight className="w-6 h-6 text-slate-300" />
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center border-2 border-emerald-200">
                <div className="text-sm font-medium text-emerald-600 mb-1">BESS</div>
                <div className="text-lg font-semibold text-[#10B981]">Speicher</div>
              </div>
              <div className="hidden lg:flex justify-center">
                <ArrowRight className="w-6 h-6 text-slate-300" />
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-slate-200">
                <div className="text-sm font-medium text-slate-500 mb-1">Krankenhaus</div>
                <div className="text-lg font-semibold text-[#0F172A]">Verbraucher</div>
              </div>
            </div>
            
            <div className="mt-6 grid lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <div className="text-sm text-slate-500 mb-1">Optional</div>
                <div className="text-base font-medium text-[#0F172A]">PV-Anlage</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <div className="text-sm text-slate-500 mb-1">Integration</div>
                <div className="text-base font-medium text-[#0F172A]">Notstromaggregat</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <div className="text-sm text-slate-500 mb-1">Anbindung</div>
                <div className="text-base font-medium text-[#0F172A]">Gebäudeleittechnik</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Battery Technology */}
      <section className="section-padding bg-slate-50" data-testid="battery-tech">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Zelltechnologie</span>
              <h2 className="heading-lg text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Lithium-Eisenphosphat (LFP)
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Wir setzen auf LFP-Technologie – die sicherste und langlebigste Lithium-Ionen-Variante, 
                speziell geeignet für kritische Infrastruktur.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Shield, title: 'Maximale Sicherheit', desc: 'Keine Brandgefahr, kein thermisches Durchgehen' },
                  { icon: Gauge, title: 'Lange Lebensdauer', desc: '6.000+ Vollzyklen, 15-20 Jahre Betriebszeit' },
                  { icon: Thermometer, title: 'Temperaturstabil', desc: 'Zuverlässig von -20°C bis +60°C' },
                  { icon: Lock, title: 'Wartungsarm', desc: 'Keine regelmäßige Wartung erforderlich' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Technische Spezifikationen
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Kapazität</span>
                  <span className="font-medium text-[#0F172A]">100 kWh - 10 MWh (modular)</span>
                </div>
                <div className="flex justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Nennspannung</span>
                  <span className="font-medium text-[#0F172A]">400V / 690V AC</span>
                </div>
                <div className="flex justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Wirkungsgrad</span>
                  <span className="font-medium text-[#10B981]">> 95% Round-Trip</span>
                </div>
                <div className="flex justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Reaktionszeit</span>
                  <span className="font-medium text-[#0F172A]">&lt; 20 ms</span>
                </div>
                <div className="flex justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Lebensdauer</span>
                  <span className="font-medium text-[#0F172A]">6.000+ Vollzyklen</span>
                </div>
                <div className="flex justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Garantie</span>
                  <span className="font-medium text-[#0F172A]">10 Jahre</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-600">Zertifizierungen</span>
                  <span className="font-medium text-[#0F172A]">TÜV, CE, IEC 62619</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="section-padding bg-white" data-testid="safety">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sicherheit auf höchstem Niveau
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Mehrfach redundante Sicherheitssysteme schützen Menschen und Infrastruktur.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="card-feature">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Zellüberwachung (BMS)
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Individuelle Zellüberwachung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Temperaturmanagement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Balancing-Funktion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Schutz vor Über-/Unterspannung</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="card-feature">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Brandschutz
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Brandfrüherkennung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Automatische Löschanlage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Feuerwiderstandsklasse F90</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Thermische Trennung der Module</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="card-feature">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Elektrische Sicherheit
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>DC/AC Trennschalter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Erdschlussüberwachung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Überstromschutz</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span>Blitz- und Überspannungsschutz</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0F172A] text-white" data-testid="technology-cta">
        <div className="container-main text-center">
          <h2 className="heading-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Technische Fragen?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Unsere Ingenieure beraten Sie gerne zu allen technischen Details und 
            erstellen ein maßgeschneidertes Konzept für Ihr Krankenhaus.
          </p>
          <Button 
            onClick={onContactClick}
            className="bg-[#10B981] hover:bg-emerald-600 text-white px-8 py-4 rounded-md font-medium inline-flex items-center gap-2"
            data-testid="technology-final-cta"
          >
            Technische Beratung anfragen
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </main>
  );
};
